import { createReducer } from 'lib/redux/reducerUtils'
import type { ValueOf } from 'type-fest'
import { isBundleOffer } from 'lib/offer/offerTypes'
import config from 'constants/config'
import { isBundleAndSaveV2Enabled } from 'lib/offer/offerUtils'
import { arrayToMap, EmptyArray } from 'lib/array/arrayUtils'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import type { Reducer } from 'react'
import uuidV4 from 'lib/string/uuidV4Utils'
import { defaultFareClass, FlightsClassTypes, type FareClassWithReward } from 'constants/flight'
import { isSkyLoungeCompatible } from 'lib/flights/flightUtils'

export enum OfferPageActions {
  checkAvailability = 'checkAvailability',
  closeFilter = 'closeFilter',
  clearFilter = 'clearFilter',
  closeSubFilter = 'closeSubFilter',
  filterContinue = 'filterContinue',
  filterBack = 'filterBack',
  resetDates = 'resetDates',
  resetDatesToMinimumDuration = 'resetDatesToMinimumDuration',
  setDepartureMonth = 'selectDepartureMonthIndex',
  setDepartureYear = 'selectDepartureYear',
  setTourV2Departure = 'selectTourV2OfferDeparture',
  setTourV2Variation = 'selectTourVOfferVariation',
  setAirport = 'setAirport',
  setPaginatorCurrentOfferId = 'setPaginatorCurrentOfferId',
  setBedBankOffer = 'setBedBankOffer',
  setCheckInDate = 'setCheckInDate',
  setCheckOutDate = 'setCheckOutDate',
  setDates = 'setDates',
  setCalendarDates = 'setCalendarDates',
  showRecommendationsView = 'showRecommendationsView',
  hideRecommendationsView = 'hideRecommendationsView',
  setDuration = 'setDuration',
  setFilteredBundlePackageId = 'setFilteredBundlePackageId',
  reverseBundles = 'reverseBundles',
  setFlights = 'setFlights',
  setFareClass = 'setFareClass',
  setOffer = 'setOffer',
  setRoomType = 'setRoom',
  setRooms = 'setRooms',
  setMultiRoomAutoselectionReason = 'setMultiRoomAutoselectionReason',
  setTourV2Offer = 'setTourV2Offer',
  toggleFilter = 'toggleFilter',
  openSwapModal = 'openSwapModal',
  toggleSubFilter = 'toggleSubFilter',
  setRoomOption = 'setRoomOption',
  selectRoomsPackages = 'selectRoomsPackages',
  selectRoomPackage = 'selectRoomPackage',
  selectBedbankRoomPackage = 'selectBedbankRoomPackage',
  toggleFlightsPrice = 'toggleFlightsPrice',
  setBedbankRoomId = 'setBedbankRoomId',
  setDisplayMemberPrice = 'setDisplayMemberPrice',
  toggleConnectedRooms = 'toggleConnectedRooms',
  setKeepBrowsing = 'setKeepBrowsing',
  setLastInteractedCalendarDay = 'setLastInteractedCalendarDay',
  toggleLoyaltyTargetRoomUpgrade = 'toggleLoyaltyTargetRoomUpgrade',
  toggleHeaderFilter = 'toggleHeaderFilter',
  closeHeaderFilter = 'closeHeaderFilter',
  toggleMultiRoomMode = 'toggleMultiRoomMode',
  toggleMultiRoomSelectRooms = 'toggleMultiRoomSelectRooms',
  setPriceMode = 'setPriceMode',
  setCharterBundleEnabled = 'setCharterBundleEnabled',
  setCharterBundleCheckoutRange = 'setCharterBundleCheckoutRange',
  /* ******************** */
  /* Single Property Page */
  /* ******************** */
  setProperty = 'setProperty',
  setPropertyRoomId = 'setPropertyRoomId',
  clearQuickFilters = 'clearQuickFilters',
  setQuickFilters = 'setQuickFilters',
  setAlternativeDatesFlow = 'setAlternativeDatesFlow',
  /* ******************** */
  /* Société Points       */
  /* ******************** */
  setLoyaltyPointsToApply = 'setLoyaltyPointsToApply',
}

export type OfferPageFilterType = 'all' | 'duration' | 'capacity' | 'airport' | 'checkin' | 'package' | 'room' | 'roomSelection' | 'packageSelection' | 'flights-toggle' | 'roomDetails' | 'multiRoomSelect' | 'multiRoomConfirm'
export type OfferPageMultiRoomMode = 'individual' | 'same-room'
export type OfferPageMultiRoomAutoselectionReason = 'initial-selection' | 'invalid-selections'
export type OfferPagePriceMode = 'total' | 'perPerson'

export const offerPagePriceModes: Array<OfferPagePriceMode> = ['total', 'perPerson']
export const offerPagePriceModeLabel: Record<OfferPagePriceMode, string> = {
  total: 'Total price',
  'perPerson': 'Price per person',
}

export type SelectedMultipleRoomPackage = {
  roomId: string
  packageId: string
  roomRateId: string
  isFlightsSelected: boolean
  roomTypeId: string
}
export type MultipleRoomPackage = SelectedMultipleRoomPackage | {
  roomId: string
  packageId: undefined
  roomRateId: undefined
  isFlightsSelected: boolean
  roomTypeId: undefined
}

export interface OfferPageQuickFilters {
  breakfastIncluded: boolean
  freeCancellation: boolean
}

export interface OfferPageRoom extends App.Occupants {
  /**
   * A unique id identifying this room
   */
  id: string
  /**
   * The ID of the room type (for bedbank) or package (for LE offers) that has been selected for this room
   */
  roomId?: string
  /**
   * The ID of the rate (for bedbank) or room rate (for LE offers) has been selected for this room
   */
  rateId?: string
}

export interface OfferPageRoomOption {
  roomRateId: string
  roomRateGroupId?: string
  roomTypeId: string
}

export interface BundledOfferState {
  offer?: App.HotelOffer
  checkInDate?: string
  checkOutDate?: string
  duration?: number
  room?: App.PackageRoomType
  activeFilter?: OfferPageFilterType
}

export interface PaginatorState {
  currentOfferId?: string
  nextOfferId?: string
  backOfferId?: string
}

export interface TourV2MonthFilter {
  month: number
  year: number
}

export interface OfferPageState {
  /**
   * The current active filter.
   * These filters correspond to showing a certain filter UI
   */
  activeFilter?: OfferPageFilterType
  /**
   * The current active 'sub' filter.
   * A sub filter is a filter that appears *inside* of an existing filter
   * It's a second-layer filter
   */
  activeSubFilter?: OfferPageFilterType
  activeHeaderFilter?: OfferPageFilterType
  /**
   * A history (as a stack) of the filters that have been triggered
   * This is used to determine how to go back to the previous filter
   */
  filterHistory: Array<OfferPageFilterType>
  /**
   * Currently selected airport for the page
   * All flight details will use this value as the departure airport
   */
  airport?: App.Airport
  /**
   * The current selected check in date
   * This field is often combined with checkOutDate to determine the duration
   * of the stay
   */
  checkInDate?: string
  /**
   * This field is populated bedbank and property page calendars
   * as they choose dates explicitly, and not selecting a duration
   */
  checkOutDate?: string
  /**
   * Number of nights/days between check-in and check-out
   * Note: This is not used for bedbank/property pages but is
   * maintained as check check in/checkout dates change
   */
  duration: number
  /**
   * Whether or not the duration has been selceted at all by the user
   * This is used to determine which 'step' the user should see
   * in the room flow
   */
  durationSelected?: boolean
  /**
   * Whether or not we are including flights bundled in with the
   * package option we are going to purchase
   * @default false
   */
  flights: boolean
  /**
   * Whther or not the user wishes for their rooms to be connected
   * note: This does not guarentee they will, just that wish they were
   */
  isMultiRoomConnected?: boolean
  /**
   * The offer this page is for
   * Note: This shares a state with bedbankOffer
   */
  offer?: App.HotelOffer | App.BundleOffer | App.VillaOffer
  /**
   * The (Bedbank) offer this page is for
   */
  bedbankOffer?: App.BedbankOffer
  /**
   * The room type selected for the page
   * This will filter results down to only this room type
   * e.g. only calendar available for this room
   */
  room?: App.PackageRoomType
  roomOption?: OfferPageRoomOption
  /**
   * A list of rooms the user will be booking for the page
   * This contains information regarding the type and count of guests in
   * each room.
   *
   * For multi-room scenarios, also stores the details of exactly which package
   * is assigned to each room
   */
  rooms: Array<OfferPageRoom>
  /**
   * The type of multi-room mode the page is in
   * @default individual
   */
  multiRoomMode: OfferPageMultiRoomMode
  /**
   * Why rooms were automatically selected, if they have been
   */
  multiRoomAutoselectionReason?: OfferPageMultiRoomAutoselectionReason
  /**
   * Whether or not we're displaying member (lux plus) prices
   */
  displayMemberPrice?: boolean
  /**
   * Bedbank room selected
   */
  selectedBedbankRoomId?: string

  isMultiRoomSelectRooms?: boolean
  keepBrowsing?: boolean

  // Bundle offer page values
  filteredBundlePackageId?: string
  bundleOffer?: App.BundleOffer
  bundledOffers: Record<string, BundledOfferState>
  bundleSwapModalTicker: number
  paginator?: PaginatorState

  // recommendations
  recommendationsCheckInDay?: string
  recommendationsMonthIndex?: string
  showRecommendationsView?: boolean

  /**
   * The unique key of the
   */
  selectedPackageKey?: string

  // Tours offer page values
  tourV2Offer?: App.Tours.TourV2Offer
  tourV2Departure?: App.Tours.TourV2OfferDeparture
  tourV2Variation?: App.Tours.TourV2OfferVariation
  departureMonth?: TourV2MonthFilter
  departureYear?: number

  // Last Interacted OfferCalendarDayCell - Only used for analytics currently
  lastInteractedCalendarDay?: App.CalendarDay

  /**
   * Record of room id to whether or not that room has been selected
   * for a loyalty upgrade or not.
   */
  loyaltyTargetStayUpgradesSelected?: App.LoyaltyTargetStayUpgradesSelected

  /**
   * Flight charter bundle info
   */
  charterBundleRoute?: App.CharterBundleRoute
  charterBundleEnabled: boolean
  charterBundleCheckoutRange?: {
    minCheckOut?: string
    maxCheckOut?: string
  }
  fareClass: FareClassWithReward

  priceMode: OfferPagePriceMode
  isPriceModeChangedFromDefault: boolean

  /* ******************** */
  /* Single Property Page */
  /* ******************** */
  property?: App.Property.Property
  propertyRoomId?: string
  /**
   * Client-side room/rate filters, not to be confused with `OfferPageFilterType`
   *
   * For now, only SPP implements this. If another hotel offer type (like Flash) also implements
   * this, feel free to relocate out of the SPP section.
   */
  quickFilters?: Partial<OfferPageQuickFilters>
  isAlternativeDatesFlow?: boolean

  /* ******************** */
  /* Société Points       */
  /* ******************** */
  /**
   * Number of Société points the user wants to apply to package pricing
   * When set, package prices will display as "X pts + $Y" format
   */
  pointsToApply?: number
  /**
   * When true, hides the "+ $0" cash remainder in points price displays.
   * Used by the full-points toggle mode where the cash element is not applicable.
   */
  pointsHideZeroCash?: boolean
}

export function generateOfferPageRoom(
  occupants: Partial<OfferPageRoom> = {},
  options: {
    packageOption?: App.HotelPackage
    rate?: App.BedbankRate
  } = {},
): OfferPageRoom {
  const { id = uuidV4(), adults = 2, children = 0, infants = 0, teenagers = 0, childrenAge = [], rateId, roomId } = occupants

  const room: OfferPageRoom = {
    id,
    adults,
    children,
    infants,
    teenagers,
    childrenAge,
    rateId,
    roomId,
  }

  if (options.packageOption) {
    room.roomId = options.packageOption.id
    room.rateId = options.packageOption.roomRate.id
  } else if (options.rate) {
    room.roomId = options.rate?.roomId
    room.rateId = options.rate?.id
  }

  return room
}

export const initialOfferPageRooms: Array<OfferPageRoom> = [generateOfferPageRoom()]

export const INITIAL_OFFER_PAGE_STATE: OfferPageState = {
  filterHistory: [],
  bundledOffers: {},
  bundleSwapModalTicker: 0,
  duration: 0,
  flights: false,
  multiRoomMode: 'individual',
  rooms: initialOfferPageRooms,
  fareClass: defaultFareClass,
  priceMode: 'total',
  isPriceModeChangedFromDefault: false,
  charterBundleEnabled: false,
  charterBundleCheckoutRange: undefined,
}

export type OfferPageDispatchAction = ValueOf<Utils.FullActionMap<{
  [OfferPageActions.checkAvailability]: {
    flights: OfferPageState['flights']
    room?: OfferPageState['room']
    filter: OfferPageState['activeFilter']
    selectedPackageKey?: string
  }
  [OfferPageActions.closeFilter]: {
    occupancyPicker?: boolean
  }
  [OfferPageActions.clearFilter]: {}
  [OfferPageActions.closeSubFilter]: {}
  [OfferPageActions.closeHeaderFilter]: {}
  [OfferPageActions.filterContinue]: {}
  [OfferPageActions.filterBack]: {}
  [OfferPageActions.resetDates]: {
    offerId?: string
  }
  [OfferPageActions.resetDatesToMinimumDuration]: {
    minimumDuration: number
    offerId?: string
  }
  [OfferPageActions.setDepartureMonth]: {
    month: OfferPageState['departureMonth'] | undefined
  }
  [OfferPageActions.setDepartureYear]: {
    year: OfferPageState['departureYear']
  }
  [OfferPageActions.setTourV2Departure]: {
    departure: OfferPageState['tourV2Departure']
  }
  [OfferPageActions.setTourV2Variation]: {
    variation: OfferPageState['tourV2Variation']
  }
  [OfferPageActions.setAirport]: {
    airport: OfferPageState['airport']
  }
  [OfferPageActions.setFareClass]: {
    fareClass: FareClassWithReward
  }
  [OfferPageActions.setBedBankOffer]: {
    bedbankOffer: OfferPageState['bedbankOffer']
  }
  [OfferPageActions.setPaginatorCurrentOfferId]: {
    currentOfferId: string
  }
  [OfferPageActions.setCheckInDate]: {
    checkIn: OfferPageState['checkInDate']
    offerId?: string
  }
  [OfferPageActions.setCheckOutDate]: {
    checkOut: OfferPageState['checkOutDate']
    offerId?: string
  }
  [OfferPageActions.setDates]: {
    checkIn: string | undefined
    checkOut: string | undefined
    offerId?: string
  }
  [OfferPageActions.setCalendarDates]: {
    checkIn: string | undefined
    checkOut: string | undefined
    minimumDuration: number
    offerId?: string
  }
  [OfferPageActions.setDuration]: {
    duration: OfferPageState['duration']
    offerId?: string
  }
  [OfferPageActions.setFilteredBundlePackageId]: {
    filteredBundlePackageId: OfferPageState['filteredBundlePackageId']
  }
  [OfferPageActions.reverseBundles]: {}
  [OfferPageActions.setFlights]: {
    flights: OfferPageState['flights']
  }
  [OfferPageActions.setOffer]: {
    offer: OfferPageState['offer']
    bundledOffers?: OfferPageState['bundledOffers']
  }
  [OfferPageActions.setRoomType]: {
    room: OfferPageState['room']
    offerId?: string
  }
  [OfferPageActions.setRooms]: {
    rooms: OfferPageState['rooms']
  }
  [OfferPageActions.setMultiRoomAutoselectionReason]: {
    reason: OfferPageMultiRoomAutoselectionReason | undefined
  }
  [OfferPageActions.setTourV2Offer]: {
    variation: OfferPageState['tourV2Variation']
    offer: OfferPageState['tourV2Offer']
  }
  [OfferPageActions.setRoomOption]: {
    roomOption: OfferPageState['roomOption']
  }
  [OfferPageActions.toggleFilter]: {
    filter: OfferPageState['activeFilter']
    keepBrowsing?: OfferPageState['keepBrowsing']
    offerId?: string
  }
  [OfferPageActions.openSwapModal]: {}
  [OfferPageActions.toggleSubFilter]: {
    filter: OfferPageState['activeSubFilter']
  }
  [OfferPageActions.showRecommendationsView]: {
    monthIndex?: string
    checkInDay?: string
  }
  [OfferPageActions.hideRecommendationsView]: {
  }
  [OfferPageActions.selectRoomsPackages]: {
    roomsPackages: Array<{ room: OfferPageRoom, packageOption: App.HotelPackage }>
  }
  [OfferPageActions.selectRoomPackage]: {
    packageOption: App.HotelPackage
    room: OfferPageRoom
  }
  [OfferPageActions.selectBedbankRoomPackage]: {
    packageOption: App.BedbankPackage
    rate?: App.BedbankRate
    room: OfferPageRoom
  }
  [OfferPageActions.toggleFlightsPrice]: {
    isFlightsSelected: boolean
  }
  [OfferPageActions.setBedbankRoomId]: {
    bedbankRoomId: OfferPageState['selectedBedbankRoomId']
  }
  [OfferPageActions.setDisplayMemberPrice]: {
    displayMemberPrice: OfferPageState['displayMemberPrice']
  }
  [OfferPageActions.toggleConnectedRooms]: {}
  [OfferPageActions.setKeepBrowsing]: {
    keepBrowsing: OfferPageState['keepBrowsing']
  }
  [OfferPageActions.setLastInteractedCalendarDay]: {
    lastInteractedCalendarDay: OfferPageState['lastInteractedCalendarDay']
  }
  [OfferPageActions.toggleLoyaltyTargetRoomUpgrade]: {
    selectedUpgradeKey: string
    selectedTargetUpgradeType: App.LoyaltyStayUpgradeType | undefined
  }
  [OfferPageActions.toggleHeaderFilter]: {
    filter: OfferPageState['activeHeaderFilter']
  }
  [OfferPageActions.toggleMultiRoomMode]: {
    multiRoomMode: OfferPageState['multiRoomMode']
  }
  [OfferPageActions.toggleMultiRoomSelectRooms]: {
    isMultiRoomSelectRooms: boolean
  }
  [OfferPageActions.setPriceMode]: {
    priceMode: OfferPagePriceMode
  }
  [OfferPageActions.setCharterBundleEnabled]: {
    charterBundleEnabled: OfferPageState['charterBundleEnabled']
  }
  [OfferPageActions.setCharterBundleCheckoutRange]: {
    charterBundleCheckoutRange: OfferPageState['charterBundleCheckoutRange']
  }
  /* ******************** */
  /* Single Property Page */
  /* ******************** */
  [OfferPageActions.setProperty]: {
    property: OfferPageState['property']
  }
  [OfferPageActions.setPropertyRoomId]: {
    roomId: OfferPageState['propertyRoomId']
  }
  [OfferPageActions.clearQuickFilters]: {}
  [OfferPageActions.setQuickFilters]: {
    quickFilters: Partial<OfferPageQuickFilters>
  }
  [OfferPageActions.setAlternativeDatesFlow]: {
    isAlternativeDatesFlow: OfferPageState['isAlternativeDatesFlow']
  }
  /* ******************** */
  /* Société Points       */
  /* ******************** */
  [OfferPageActions.setLoyaltyPointsToApply]: {
    pointsToApply: OfferPageState['pointsToApply']
    hideZeroCash?: OfferPageState['pointsHideZeroCash']
  }
}>>

function setBundledOfferData(
  offerId: string,
  field: 'checkInDate' | 'checkOutDate' | 'duration' | 'room' | 'activeFilter',
  state: Partial<OfferPageState>,
  value: OfferPageState['checkInDate'] | OfferPageState['checkOutDate'] | OfferPageState['duration'] | OfferPageState['room'] | OfferPageState['activeFilter'],
) {
  return {
    bundledOffers: {
      ...(state.bundledOffers ?? {}),
      [offerId]: {
        ...(state.bundledOffers?.[offerId] ?? {}),
        [field]: value,
      },
    },
  }
}

export function takeOffersStates(state: OfferPageState) {
  if (isBundleOffer(state.offer)) {
    const attachedOffersIds = Object.keys(state.bundledOffers)

    return attachedOffersIds.map((id) => takeOfferState(state, id))
  }

  return [takeOfferState(state)]
}

export function takeOfferState(state: OfferPageState, offerId?: string): OfferPageState {
  if (isBundleOffer(state.offer) && offerId) {
    return {
      ...state,
      ...state.bundledOffers?.[offerId] ?? {},
    } as any
  }

  return state
}

export function coverByOffersStates<T extends any>(selector: any, ...states: Array<OfferPageState | App.State>): Array<T> {
  const offerPageStateIndex = states.findIndex((state) => 'id' in state.offer!)
  const offerPageStates = takeOffersStates(states[offerPageStateIndex] as OfferPageState)
  return offerPageStates.map((state) => {
    const newState = [...states]
    newState[offerPageStateIndex] = state
    return selector(...newState)
  })
}

export function takeCurrentOfferState(state: OfferPageState) {
  return takeOfferState(state, state.paginator?.currentOfferId)
}

function offerPageStateReducer(middleware: Array<any> = EmptyArray, log: boolean = false): Reducer<OfferPageState, OfferPageDispatchAction> {
  return createReducer<OfferPageState, OfferPageDispatchAction>(INITIAL_OFFER_PAGE_STATE, {
    [OfferPageActions.toggleConnectedRooms]: (state) => ({
      isMultiRoomConnected: !state.isMultiRoomConnected,
    }),
    [OfferPageActions.checkAvailability]: (state, action) => ({
      flights: action.flights,
      room: action.room,
      activeFilter: action.filter,
      selectedPackageKey: action.selectedPackageKey,
    }),
    [OfferPageActions.closeFilter]: (state, action) => ({
      activeFilter: undefined,
      ...(!action.occupancyPicker && { selectedPackageKey: undefined }),
    }),
    [OfferPageActions.filterContinue]: () => ({
      activeFilter: undefined,
    }),
    [OfferPageActions.filterBack]: () => ({
      activeFilter: undefined,
    }),
    [OfferPageActions.clearFilter]: () => ({
      activeFilter: undefined,
    }),
    [OfferPageActions.closeSubFilter]: () => ({
      activeSubFilter: undefined,
    }),
    [OfferPageActions.closeHeaderFilter]: () => ({
      activeHeaderFilter: undefined,
    }),
    [OfferPageActions.resetDates]: (state, action) => {
      if (action.offerId) {
        return setBundledOfferData(action.offerId, 'checkOutDate', setBundledOfferData(action.offerId, 'checkInDate', state, undefined), undefined)
      }

      return {
        checkInDate: undefined,
        checkOutDate: undefined,
      }
    },
    [OfferPageActions.resetDatesToMinimumDuration]: (state, action) => {
      if (action.offerId) {
        return setBundledOfferData(action.offerId, 'checkOutDate', setBundledOfferData(action.offerId, 'checkInDate', state, undefined), undefined)
      }

      return {
        checkInDate: undefined,
        checkOutDate: undefined,
        duration: action.minimumDuration,
        selectedPackageKey: action.minimumDuration !== state.duration ? undefined : state.selectedPackageKey,
      }
    },
    [OfferPageActions.setDepartureMonth]: (state, action) => ({
      departureMonth: action.month,
    }),
    [OfferPageActions.setDepartureYear]: (state, action) => {
      if (
        // we're setting a year and we have a month already set
        state.departureMonth && action.year &&
        // the years don't match
        state.departureMonth.year !== action.year
      ) {
        // reset the selected 'month' if it's no longer the same year as the one the user selects
        return {
          departureYear: action.year,
          departureMonth: undefined,
        }
      }
      return {
        departureYear: action.year,
      }
    },
    [OfferPageActions.setTourV2Departure]: (state, action) => ({
      tourV2Departure: action.departure,
    }),
    [OfferPageActions.setTourV2Variation]: (state, action) => ({
      tourV2Variation: action.variation,
    }),
    [OfferPageActions.setAirport]: (state, action) => ({
      airport: action.airport,
    }),
    [OfferPageActions.setFareClass]: (state, action) => ({
      fareClass: action.fareClass,
    }),
    [OfferPageActions.setBedBankOffer]: (state, action) => ({
      bedbankOffer: action.bedbankOffer,
    }),
    [OfferPageActions.setPaginatorCurrentOfferId]: (state, action) => {
      const allOffersIds = Object.keys(state.bundledOffers ?? {})
      const paginator = {
        currentOfferId: action.currentOfferId,
        nextOfferId: allOffersIds[allOffersIds.indexOf(action.currentOfferId) + 1],
        backOfferId: allOffersIds[allOffersIds.indexOf(action.currentOfferId) - 1],
      }

      if (paginator.nextOfferId && state.bundledOffers?.[paginator.nextOfferId]?.checkInDate && state.bundledOffers?.[paginator.currentOfferId]?.checkInDate) {
        if (isBundleAndSaveV2Enabled()) {
          return {
            paginator,
          }
        }
        return {
          paginator,
          ...setBundledOfferData(paginator.nextOfferId, 'checkOutDate', setBundledOfferData(paginator.nextOfferId, 'checkInDate', state, undefined), undefined),
        }
      }

      return {
        paginator,
      }
    },
    [OfferPageActions.setDates]: (state, action) => {
      if (action.offerId) {
        return setBundledOfferData(action.offerId, 'checkInDate', state, action.checkIn)
      }

      let duration = state.duration
      if (action.checkIn && action.checkOut) {
        duration = moment(action.checkOut).diff(action.checkIn, 'days')
      }

      return {
        checkOutDate: action.checkOut,
        checkInDate: action.checkIn,
        durationSelected: true,
        duration,
        selectedPackageKey: duration !== state.duration ? undefined : state.selectedPackageKey,
      }
    },
    [OfferPageActions.setCalendarDates]: (state, action) => {
      if (action.offerId) {
        return setBundledOfferData(action.offerId, 'checkInDate', state, action.checkIn)
      }

      const duration = action.checkIn && action.checkOut
        ? moment(action.checkOut).diff(action.checkIn, 'days')
        : action.minimumDuration

      return {
        checkOutDate: action.checkOut,
        checkInDate: action.checkIn,
        durationSelected: true,
        duration,
        selectedPackageKey: duration !== state.duration ? undefined : state.selectedPackageKey,
      }
    },
    [OfferPageActions.setCheckInDate]: (state, action) => {
      let checkOutDate: string | undefined = state.checkOutDate
      if (state.duration) {
        // need to auto update check out to keep it in sync if we have a duration
        if (action.checkIn) {
          checkOutDate = moment(action.checkIn).add(state.duration, 'days').format(ISO_DATE_FORMAT)
        } else {
          checkOutDate = undefined
        }
      }
      if (action.offerId) {
        return setBundledOfferData(action.offerId, 'checkInDate', state, action.checkIn)
      }

      return {
        checkOutDate,
        checkInDate: action.checkIn,
        durationSelected: true,
      }
    },
    [OfferPageActions.setCheckOutDate]: (state, action) => {
      if (action.offerId) {
        return setBundledOfferData(action.offerId, 'checkOutDate', state, action.checkOut)
      }

      let duration = state.duration
      if (state.checkInDate && action.checkOut) {
        duration = moment(action.checkOut).diff(state.checkInDate, 'days')
      }

      return {
        checkOutDate: action.checkOut,
        duration,
        durationSelected: true,
      }
    },
    [OfferPageActions.setDuration]: (state, action) => {
      if (action.offerId) {
        return setBundledOfferData(action.offerId, 'duration', state, action.duration)
      }

      let checkOutDate: string | undefined = state.checkOutDate
      if (action.duration) {
        // need to auto update check out to keep it in sync
        if (state.checkInDate) {
          checkOutDate = moment(state.checkInDate).add(action.duration, 'days').format(ISO_DATE_FORMAT)
        }
      }

      return {
        checkOutDate,
        duration: action.duration,
        // duration changed, the selected package view is no longer possible, so clear it
        selectedPackageKey: action.duration !== state.duration ? undefined : state.selectedPackageKey,
        durationSelected: true,
      }
    },
    [OfferPageActions.setFilteredBundlePackageId]: (state, action) => ({
      filteredBundlePackageId: action.filteredBundlePackageId,
    }),
    [OfferPageActions.reverseBundles]: (state) => {
      const [offerStateOne, offerStateTwo] = Object.values(state.bundledOffers)
      if (!offerStateOne?.offer || !offerStateTwo?.offer) {
        return state
      }

      const newOrderedObject: Record<string, BundledOfferState> = {}
      newOrderedObject[offerStateTwo.offer.id] = offerStateTwo
      newOrderedObject[offerStateOne.offer.id] = offerStateOne
      return {
        bundledOffers: newOrderedObject,
      }
    },
    [OfferPageActions.setFlights]: (state, action) => ({
      flights: action.flights,
    }),
    [OfferPageActions.setOffer]: (state, action) => {
      return {
        bundleOffer: isBundleOffer(action.offer) ? action.offer : undefined,
        offer: action.offer,
        bundledOffers: action.bundledOffers,
      }
    },
    [OfferPageActions.setRoomType]: (state, action) => {
      if (action.offerId) {
        return setBundledOfferData(action.offerId, 'room', state, action.room)
      }

      return {
        room: action.room,
        // room changed, selected view is no longer possible as it was for a previous room
        selectedPackageKey: action.room !== state.room ? undefined : state.selectedPackageKey,
      }
    },
    [OfferPageActions.setRooms]: (state, action) => {
      const shouldResetFareClass = state.fareClass === FlightsClassTypes.LUXROW && !isSkyLoungeCompatible(action.rooms)
      return {
        rooms: action.rooms,
        ...(shouldResetFareClass && { fareClass: FlightsClassTypes.ECONOMY }),
      }
    },
    [OfferPageActions.setMultiRoomAutoselectionReason]: (state, action) => ({
      multiRoomAutoselectionReason: action.reason,
    }),
    [OfferPageActions.setBedbankRoomId]: (state, action) => ({
      selectedBedbankRoomId: action.bedbankRoomId,
    }),
    [OfferPageActions.setTourV2Offer]: (state, action) => ({
      tourV2Variation: action.variation,
      tourV2Offer: action.offer,
    }),
    [OfferPageActions.setRoomOption]: (state, action) => ({
      roomOption: action.roomOption,
    }),
    [OfferPageActions.toggleFilter]: (state, action) => {
      if (action.offerId) {
        const filterShouldBeClosed = action.filter === state.bundledOffers[action.offerId].activeFilter
        if (filterShouldBeClosed) {
          return setBundledOfferData(action.offerId, 'activeFilter', state, undefined)
        }
        return setBundledOfferData(action.offerId, 'activeFilter', state, action.filter)
      }

      const filterShouldBeClosed = action.filter === state.activeFilter
      if (filterShouldBeClosed) {
        return {
          activeFilter: undefined,
        }
      }

      return {
        activeFilter: action.filter,
      }
    },
    [OfferPageActions.openSwapModal]: (state) => {
      return {
        bundleSwapModalTicker: state.bundleSwapModalTicker + 1,
      }
    },
    [OfferPageActions.toggleSubFilter]: (state, action) => {
      const filterShouldBeClosed = action.filter === state.activeSubFilter
      if (filterShouldBeClosed) {
        return {
          activeSubFilter: undefined,
        }
      }

      return {
        activeSubFilter: action.filter,
      }
    },
    [OfferPageActions.showRecommendationsView]: (state, action) => ({
      showRecommendationsView: true,
      recommendationsMonthIndex: action.monthIndex ?? state.recommendationsMonthIndex,
      recommendationsCheckInDay: action.checkInDay ?? state.recommendationsCheckInDay,
    }),
    [OfferPageActions.hideRecommendationsView]: () => ({
      showRecommendationsView: false,
      recommendationsMonthIndex: undefined,
      recommendationsCheckInDay: undefined,
    }),
    [OfferPageActions.selectRoomsPackages]: (state, action) => {
      const { roomsPackages } = action
      const packagesById = arrayToMap(roomsPackages, ({ room }) => room.id, ({ packageOption }) => packageOption)

      return {
        rooms: state.rooms.map(room => {
          const packageOption = packagesById.get(room.id)
          if (packageOption) {
            return {
              ...room,
              roomId: packageOption.id,
              rateId: packageOption.roomRate.id,
            }
          }
          return room
        }),
        multiRoomAutoselectionReason: undefined,
      }
    },
    [OfferPageActions.selectRoomPackage]: (state, action) => {
      const { packageOption, room: updatedRoom } = action

      return {
        rooms: state.rooms.map(room => {
          if (room.id === updatedRoom.id) {
            return {
              ...room,
              roomId: packageOption.id,
              rateId: packageOption.roomRate.id,
            }
          }
          return room
        }),
        multiRoomAutoselectionReason: undefined,
      }
    },
    [OfferPageActions.selectBedbankRoomPackage]: (state, action) => {
      const { packageOption, rate, room: updatedRoom } = action

      return {
        rooms: state.rooms.map(room => {
          if (room.id === updatedRoom.id) {
            return {
              ...room,
              roomId: packageOption.roomId,
              rateId: rate?.id,
            }
          }
          return room
        }),
        multiRoomAutoselectionReason: undefined,
      }
    },
    [OfferPageActions.toggleFlightsPrice]: (state, action) => {
      return ({
        flights: action.isFlightsSelected,
      })
    },
    [OfferPageActions.setDisplayMemberPrice]: (state, action) => ({
      displayMemberPrice: action.displayMemberPrice,
    }),
    [OfferPageActions.setKeepBrowsing]: (state, action) => ({
      keepBrowsing: action.keepBrowsing,
    }),
    [OfferPageActions.setLastInteractedCalendarDay]: (state, action) => ({
      lastInteractedCalendarDay: action.lastInteractedCalendarDay,
    }),
    [OfferPageActions.toggleLoyaltyTargetRoomUpgrade]: (state, action) => {
      return {
        loyaltyTargetStayUpgradesSelected: {
          ...state.loyaltyTargetStayUpgradesSelected,
          [action.selectedUpgradeKey]: action.selectedTargetUpgradeType,
        },
      }
    },
    [OfferPageActions.toggleHeaderFilter]: (_, action) => ({
      activeHeaderFilter: action.filter,
    }),
    [OfferPageActions.toggleMultiRoomMode]: (_, action) => ({
      multiRoomMode: action.multiRoomMode,
    }),
    [OfferPageActions.toggleMultiRoomSelectRooms]: (_, action) => ({
      isMultiRoomSelectRooms: action.isMultiRoomSelectRooms,
    }),
    [OfferPageActions.setPriceMode]: (_, action) => ({
      priceMode: action.priceMode,
      isPriceModeChangedFromDefault: true,
    }),
    [OfferPageActions.setCharterBundleEnabled]: (_, action) => ({
      charterBundleEnabled: action.charterBundleEnabled,
      flights: action.charterBundleEnabled,
    }),
    [OfferPageActions.setCharterBundleCheckoutRange]: (_, action) => ({
      charterBundleCheckoutRange: action.charterBundleCheckoutRange,
    }),
    /* ******************** */
    /* Single Property Page */
    /* ******************** */
    [OfferPageActions.setProperty]: (_state, action) => ({
      property: action.property,
    }),
    [OfferPageActions.setPropertyRoomId]: (_state, action) => ({
      propertyRoomId: action.roomId,
    }),
    [OfferPageActions.clearQuickFilters]: () => ({
      quickFilters: undefined,
    }),
    [OfferPageActions.setQuickFilters]: (state, action) => ({
      quickFilters: {
        ...state.quickFilters,
        ...action.quickFilters,
      },
    }),
    [OfferPageActions.setAlternativeDatesFlow]: (_state, action) => ({
      isAlternativeDatesFlow: action.isAlternativeDatesFlow,
    }),
    /* ******************** */
    /* Société Points       */
    /* ******************** */
    [OfferPageActions.setLoyaltyPointsToApply]: (_state, action) => ({
      pointsToApply: action.pointsToApply,
      pointsHideZeroCash: action.hideZeroCash,
    }),
  },
  middleware,
  log && config.LOG_REACT_REDUCERS,
  )
}

export default offerPageStateReducer
